import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Breadcrumb, Dropdown, Layout, Menu, MenuProps, Spin } from 'antd';
import './DefaultLayout.less';
import { Outlet, useLocation } from 'react-router-dom';
import { findAllByPath, findAncestorByPath, findMenuByPath, transform } from '@/utils/menu-utils';
import RouteLoadProgressEvent, { PageLoadFinish, PageLoadStart } from '@/events/page-load-event';
import { getLoginMenu, userPermission } from '@/services';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { currentUser } from '@/services/system/user';
import LoginEvent, { AlreadyLogin, Logout, LogoutSuccess } from '@/events/login-event';
import ModifyPassword from '@/components/ModifyPassword';
import auth from '@/utils/auth';
import { firstValueFrom, forkJoin, map } from 'rxjs';
import { UserOutlined } from '@ant-design/icons';
import AlarmNotification from '@/layout/AlarmNotification';
import PushEvent, { PermissionChange } from '@/events/push-event';

const { Header, Sider } = Layout;

// 进度条关闭Spin
NProgress.configure({ showSpinner: false });
const pageLoadStart = () => {
  NProgress.start();
};
const pageLoadFinish = () => {
  NProgress.done();
};

export const currentUserContext = createContext<API.User | null>(null);

const DefaultLayout = () => {
  const [menu, setMenu] = useState<API.Menu[]>([]);
  const [user, setUser] = useState<API.User | null>(null);
  const [modifyPasswordShow, setModifyPasswordShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const selectedParent = useRef<string[]>([]);
  const { login, logout } = auth();

  const menuData = useMemo(() => {
    const transformMenu = transform(menu);
    const selected = findMenuByPath(menu, location.pathname).map(i => i.id!);
    const parents = findAncestorByPath(menu, location.pathname).map(i => i.id!);
    const all = findAllByPath(menu, location.pathname).map(it => {
      return {
        title: it.name,
      };
    });
    selectedParent.current = findAncestorByPath(menu, location.pathname).map(i => i.id!);
    return {
      transformMenu,
      selected,
      parents,
      all,
    };
  }, [menu, location.pathname]);

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: '修改密码',
      onClick: () => {
        setModifyPasswordShow(true);
      },
    },
    {
      key: '2',
      label: '退出登录',
      onClick: () => {
        LoginEvent.emit(Logout);
      },
    },
  ];

  const logoutSuccessEventHandler = () => {
    logout();
  };

  const loadPermissionData = async () => {
    return firstValueFrom(forkJoin([
      getLoginMenu(),
      currentUser(),
      userPermission(),
    ]).pipe(map(result => {
      LoginEvent.emit(AlreadyLogin);
      const menu = result[0];
      menu.unshift({
        id: '0',
        name: '主页',
        url: '/',
        icon: 'HomeOutlined',
        order: 0,
        type: 0,
      });
      setMenu(menu);
      setUser(result[1]);
      login(result[2]);
    })));
  };

  const permissionChangeEvent = () => {
    void loadPermissionData().then();
  };

  const menuCollapseChange = (value: boolean) => {
    setCollapsed(value);
    localStorage.setItem('menu_collapsed', String(value));
    if (value) {
      setOpenKeys([]);
    } else {
      setTimeout(() => {
        setOpenKeys(selectedParent.current);
      }, 10);
    }
  };

  // 加载初始化数据
  useEffect(() => {
    RouteLoadProgressEvent.on(PageLoadStart, pageLoadStart);
    RouteLoadProgressEvent.on(PageLoadFinish, pageLoadFinish);
    LoginEvent.on(LogoutSuccess, logoutSuccessEventHandler);

    setLoading(true);
    loadPermissionData().then().finally(() => {
      setLoading(false);
      if (localStorage.getItem('menu_collapsed') === 'true') {
        menuCollapseChange(true);
      } else {
        menuCollapseChange(false);
      }
    });

    PushEvent.on(PermissionChange, permissionChangeEvent);

    return () => {
      RouteLoadProgressEvent.off(PageLoadStart, pageLoadStart);
      RouteLoadProgressEvent.off(PageLoadFinish, pageLoadFinish);
      LoginEvent.off(LogoutSuccess, logoutSuccessEventHandler);
      PushEvent.off(PermissionChange, permissionChangeEvent);
    };
  }, []);

  const computeMarginLeft = () => {
    if (collapsed) {
      return '60px';
    }
    return '250px';
  };

  return (
    loading
      ? <div className="page-loading">
        <Spin size="large"/>
      </div>
      : <currentUserContext.Provider value={user}>
        <AlarmNotification/>
        <Layout className="page-layout">
          <Header className="page-header">
            <div className="logo"></div>
            <div className="center"></div>
            <div className="avatar">
              <Dropdown menu={{ items }}>
                <span className="user-drop-menu">
                  <Avatar style={{ backgroundColor: '#87d068' }} icon={<UserOutlined/>}/>
                  <span className="user">{user?.username}</span>
                </span>
              </Dropdown>
            </div>
          </Header>
          <div className="page-header-placeholder"></div>
          <Layout hasSider className="main-layout">
            <Sider
              width={250}
              collapsedWidth={60}
              className="menu-side"
              collapsible
              collapsed={collapsed}
              onCollapse={menuCollapseChange}>
              {
                menu?.length > 0
                  ? <Menu
                    mode="inline"
                    style={{ height: '100%', borderRight: 0 }}
                    items={menuData.transformMenu}
                    selectedKeys={menuData.selected}
                    openKeys={openKeys}
                    onOpenChange={openKeys => {
                      setOpenKeys(openKeys);
                    }}
                  />
                  : null
              }
            </Sider>
            <Layout className="right-layout" style={{ marginLeft: computeMarginLeft() }}>
              <div className="page-bread-crumb">
                <Breadcrumb items={menuData.all}/>
              </div>
              <Layout className="content-layout">
                <Outlet/>
              </Layout>
            </Layout>
          </Layout>
        </Layout>
        <ModifyPassword open={modifyPasswordShow} onOpenChange={setModifyPasswordShow}/>
      </currentUserContext.Provider>

  );
};

export default DefaultLayout;
